import { Box, Skeleton } from '@mui/material';
import { useLocation } from 'react-router-dom';

/**
 * Route-aware skeleton fallback for the persistent layout's <Suspense>.
 *
 * The Suspense fallback used to be a single fullscreen <LoadingIndicator>.
 * Combined with the outer Suspense placement in App.tsx that meant every
 * route navigation flashed a centered spinner that replaced the entire
 * app shell — exactly what feels "page-y", not SPA-like.
 *
 * Now the Suspense lives inside MainLayout's <Outlet> region, so the
 * header + module rail stay visible. The skeleton mimics the rough shape
 * of the route being loaded so the user sees content boundaries appear
 * before the real data lands. No fullscreen spinner anywhere on a normal
 * navigation.
 *
 * Picking the variant is intentionally simple: branch on the first path
 * segment. We don't need to be perfect — the goal is "looks like
 * something is loading in this region" not "exactly matches the page".
 */
export function PageSkeleton() {
  const { pathname } = useLocation();
  const segment = pathname.split('/').filter(Boolean)[0] || 'mail';

  if (segment === 'calendar') return <CalendarSkeleton />;
  if (segment === 'contacts') return <ContactsSkeleton />;
  if (segment === 'settings') return <SettingsSkeleton />;
  if (segment === 'drive') return <DriveSkeleton />;
  return <MailSkeleton />;
}

function MailSkeleton() {
  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, flex: 1 }}>
      {/* Mailbox tree column */}
      <Box
        sx={{
          width: 230,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          p: 2,
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Skeleton variant="text" width="60%" height={28} />
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" width="100%" height={36} />
        ))}
      </Box>

      {/* Mail list column */}
      <Box
        sx={{
          width: { xs: '100%', sm: 400 },
          flexShrink: 0,
          borderRight: { sm: 1 },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1 }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="90%" height={16} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Email viewer column (hidden on mobile) */}
      <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', p: 3, gap: 2 }}>
        <Skeleton variant="text" width="80%" height={36} />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
        </Box>
        <Skeleton variant="rounded" width="100%" height={300} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="92%" />
        <Skeleton variant="text" width="84%" />
      </Box>
    </Box>
  );
}

function CalendarSkeleton() {
  return (
    <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton variant="rounded" width={140} height={36} />
        <Skeleton variant="text" width={180} height={32} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={70} height={32} />
          <Skeleton variant="rounded" width={70} height={32} />
          <Skeleton variant="rounded" width={70} height={32} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 3 }}>
        <Box sx={{ width: 280, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rounded" width="100%" height={240} />
          <Skeleton variant="rounded" width="100%" height={140} />
        </Box>
        <Skeleton variant="rounded" sx={{ flex: 1, minHeight: 0 }} />
      </Box>
    </Box>
  );
}

function ContactsSkeleton() {
  return (
    <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <Box
        sx={{
          width: { xs: '100%', sm: 360 },
          borderRight: { sm: 1 },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 2,
        }}
      >
        <Skeleton variant="rounded" width="100%" height={36} />
        {Array.from({ length: 12 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="80%" height={14} />
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', p: 3, gap: 2 }}>
        <Skeleton variant="circular" width={96} height={96} />
        <Skeleton variant="text" width="50%" height={32} />
        <Skeleton variant="text" width="35%" height={20} />
        <Skeleton variant="rounded" width="100%" height={120} />
      </Box>
    </Box>
  );
}

function SettingsSkeleton() {
  return (
    <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <Box
        sx={{
          width: 260,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Skeleton variant="text" width="40%" height={32} />
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" width="100%" height={36} />
        ))}
      </Box>
      <Box sx={{ flex: 1, p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="text" width="40%" height={36} />
        <Skeleton variant="rounded" width="100%" height={120} />
        <Skeleton variant="rounded" width="100%" height={80} />
        <Skeleton variant="rounded" width="60%" height={40} />
      </Box>
    </Box>
  );
}

function DriveSkeleton() {
  return (
    <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Skeleton variant="text" width="30%" height={32} />
      <Skeleton variant="rounded" sx={{ flex: 1, minHeight: 0 }} />
    </Box>
  );
}
